import React, { useEffect, useRef, useState } from 'react';
import leagueName from '../utils/leagueName';
import Modal from 'react-bootstrap/Modal';
import './AdminResultCorrections.css';
import './AdminReviewResults.css';

export default function AdminReviewResults({ user, onSettled }) {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [confirming, setConfirming] = useState(null);
  const [revision, setRevision] = useState(0);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [failures, setFailures] = useState([]);
  const submitLock = useRef(false);

  useEffect(() => {
    const controller = new AbortController();
    setConfirming(null);
    setLoading(true);
    setError('');
    setCandidates([]);
    if (!user) { setLoading(false); return () => controller.abort(); }
    (async () => {
      try {
        const token = await user.getIdToken();
        if (controller.signal.aborted) return;
        const response = await fetch('/api/admin/review-results', {
          headers: { Authorization: `Bearer ${token}` }, signal: controller.signal,
        });
        const data = await response.json();
        if (!response.ok || !data.success) throw new Error(data.error || 'Unable to load results.');
        if (!controller.signal.aborted) setCandidates(data.candidates || []);
      } catch (err) {
        if (!controller.signal.aborted) setError(err.message);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [user, revision]);

  const cancel = () => { if (!submitLock.current) setConfirming(null); };
  const submit = async () => {
    if (submitLock.current || !confirming) return;
    const { item: match, verdict } = confirming;
    submitLock.current = true;
    setSubmitting(true);
    setError('');
    setNotice('');
    setFailures([]);
    try {
      const token = await user.getIdToken();
      const response = await fetch('/api/admin/review-results', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ confirmation: 'REVIEW', selections: [{ key: match.key, reviewToken: match.reviewToken, verdict }] }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || 'Unable to submit results.');
      const result = data.results.find((item) => item.key === match.key);
      if (!result) throw new Error('No review outcome was returned.');
      if (result.success) setNotice(verdict === 'correct'
        ? `${match.fixture}: Settled ${result.betCount} bets.`
        : `${match.fixture}: Marked wrong. The match remains in Pending Results; no bets or balances were changed.`);
      setFailures(data.results.filter((item) => !item.success).map((item) => ({
        key: item.key, fixture: match.fixture, error: item.error,
      })));
    } catch (err) {
      // A lost response does not prove a payout failed; reload before any retry.
      setNotice('Submission could not be confirmed. Results have been reloaded; check Pending Results or Corrections before retrying.');
      setFailures([{ key: 'request', fixture: 'Submission', error: err.message }]);
    } finally {
      onSettled?.();
      setConfirming(null);
      setSubmitting(false);
      submitLock.current = false;
      setRevision((value) => value + 1);
    }
  };

  return <div className='col-lg-10 col-sm-12 m-auto'>
    <section className='bg-green shadow-down rounded mb-5 match-list admin-tab-panel admin-review-panel p-3 text-white text-start' aria-label='Review results'>
      <div className='d-flex justify-content-between align-items-center gap-2 mb-2'>
        <h2 className='h5 mb-0'>Review Results</h2>
        <button type='button' className='btn btn-sm btn-outline-light' disabled={loading || submitting || Boolean(confirming)}
          onClick={() => setRevision((value) => value + 1)}>Refresh</button>
      </div>
      <p className='small text-lightgrey'>Review one match at a time. Click Correct or Wrong, then confirm in the popup. Correct results settle after confirmation. Wrong results leave this queue but stay in Pending Results. Unmarked results stay in both. <br />Results are cached for up to 6 hours. Only completed 1x2 results with matching teams are shown. Nothing settles without your confirmation.</p>
      {notice && <p role='status'>{notice}</p>}
      {failures.length > 0 && <div role='alert' className='alert alert-danger'>
        {failures.map((item) => <div key={item.key}>{item.fixture}: {item.error}</div>)}
      </div>}
      {error && <p role='alert' className='text-danger'>{error}</p>}
      {loading ? <div className='text-center py-4' role='status'><span className='spinner-border text-orange' aria-hidden='true' /><span className='visually-hidden'>Loading results…</span></div>
        : !error && !candidates.length ? <p>No completed results are ready to review. Other matches can still be settled in Pending Results.</p>
          : candidates.map((item) => <div key={item.key} className='admin-review-card admin-correction-market rounded p-3 mb-2'>
            <span>
              <strong className='d-block'>{item.fixture}</strong>
              <span className='small text-lightgrey d-block'>{leagueName(item.sport_keys)}</span>
              <span className='small text-lightgrey d-block'>{new Date(`${item.match_day}T${item.time}Z`).toLocaleString()} · {item.betCount} bets · {item.bet_market}</span>
              <span className='d-block text-orange fw-bold admin-review-score'>{item.score.homeScore} – {item.score.awayScore}</span>
              <span className='d-block'>Winning outcome: <strong>{item.winner}</strong></span>
              {item.checkedAt && <span className='small text-lightgrey d-block'>Checked: {new Date(item.checkedAt).toLocaleString()}</span>}
              <span role='group' aria-label={`Review ${item.fixture}`} className='d-flex gap-2 flex-wrap mt-2'>
                {['correct', 'wrong'].map((verdict) => <button key={verdict} type='button'
                  className={`btn btn-sm admin-review-action admin-review-action--${verdict}`}
                  aria-label={`${verdict === 'correct' ? 'Correct' : 'Wrong'}: ${item.fixture}`}
                  disabled={submitting || Boolean(confirming)}
                  onClick={() => setConfirming({ item, verdict })}>
                  {verdict === 'correct' ? 'Correct' : 'Wrong'}
                </button>)}
              </span>
            </span>
          </div>)}
    </section>
    <Modal show={Boolean(confirming)} onHide={cancel} centered scrollable
      backdrop={submitting ? 'static' : true} keyboard={!submitting}
      className='admin-correction-modal text-white' backdropClassName='admin-correction-backdrop'
      aria-labelledby='review-confirm-title'>
      <Modal.Header>
        <Modal.Title id='review-confirm-title' className='h5'>
          Confirm result: <strong className={`admin-review-verdict--${confirming?.verdict}`}>{confirming?.verdict.toUpperCase()}</strong>
        </Modal.Title>
      </Modal.Header>
      {confirming && <Modal.Body>
        <strong className='d-block'>{confirming.item.fixture}</strong>
        <span className='small text-lightgrey d-block'>{leagueName(confirming.item.sport_keys)}</span>
        <span className='small text-lightgrey d-block'>{new Date(`${confirming.item.match_day}T${confirming.item.time}Z`).toLocaleString()} · {confirming.item.bet_market}</span>
        <p className='mt-2 mb-1 text-orange fw-bold admin-review-score'>{confirming.item.score.homeScore} – {confirming.item.score.awayScore}</p>
        <p>Winning outcome: <strong>{confirming.item.winner}</strong></p>
        <p className='mb-0'>{confirming.verdict === 'correct'
          ? <>Confirm this result is <strong className='admin-review-verdict--correct'>CORRECT</strong> and settle {confirming.item.betCount} bets for this match? Balances will be updated and bet notifications sent.</>
          : <>Mark this result <strong className='admin-review-verdict--wrong'>WRONG</strong>? It will leave Review Results but remain in Pending Results for manual settlement. No bets or balances will change.</>}</p>
      </Modal.Body>}
      <Modal.Footer>
        <button type='button' className='btn btn-outline-light' disabled={submitting} onClick={cancel}>Cancel</button>
        <button type='button' className={`btn fw-bold admin-review-action admin-review-action--${confirming?.verdict}`} disabled={submitting || !confirming} onClick={submit}>
          {submitting ? 'Saving…' : confirming?.verdict === 'correct' ? 'Confirm and settle' : 'Confirm wrong result'}
        </button>
      </Modal.Footer>
    </Modal>
  </div>;
}
